import { Node, mergeAttributes } from '@tiptap/core';

export interface EmailButtonOptions {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    emailButton: {
      setEmailButton: (options: {
        href: string;
        text: string;
        variant?: string;
        alignment?: string;
        size?: string;
      }) => ReturnType;
      updateEmailButton: (options: {
        href?: string;
        text?: string;
        variant?: string;
        alignment?: string;
        size?: string;
      }) => ReturnType;
    };
  }
}

const BUTTON_STYLES = {
  variants: {
    primary: {
      backgroundColor: '#FF6B35',
      color: '#ffffff',
    },
    secondary: {
      backgroundColor: '#6B7280',
      color: '#ffffff',
    },
    success: {
      backgroundColor: '#4CAF50',
      color: '#ffffff',
    },
    warning: {
      backgroundColor: '#F59E0B',
      color: '#ffffff',
    },
    danger: {
      backgroundColor: '#EF4444',
      color: '#ffffff',
    },
  },
  sizes: {
    small: {
      padding: '8px 16px',
      fontSize: '14px',
    },
    medium: {
      padding: '12px 24px',
      fontSize: '16px',
    },
    large: {
      padding: '16px 32px',
      fontSize: '18px',
    },
  },
};

function generateButtonStyle(variant: string, size: string): string {
  const variantStyle = BUTTON_STYLES.variants[variant as keyof typeof BUTTON_STYLES.variants] || BUTTON_STYLES.variants.primary;
  const sizeStyle = BUTTON_STYLES.sizes[size as keyof typeof BUTTON_STYLES.sizes] || BUTTON_STYLES.sizes.medium;

  return [
    'display: inline-block',
    `padding: ${sizeStyle.padding}`,
    `background-color: ${variantStyle.backgroundColor}`,
    `color: ${variantStyle.color}`,
    'text-decoration: none',
    'border-radius: 6px',
    'font-weight: 600',
    'font-family: Arial, Helvetica, sans-serif',
    `font-size: ${sizeStyle.fontSize}`,
    'line-height: 1.5',
    'border: none',
    'cursor: pointer',
  ].join('; ');
}

export const EmailButton = Node.create<EmailButtonOptions>({
  name: 'emailButton',

  group: 'block',

  content: 'inline*',

  atom: true,

  selectable: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      const dom = document.createElement('p');
      dom.setAttribute('data-email-button-wrapper', 'true');
      dom.setAttribute('contenteditable', 'false');
      dom.style.cssText = `text-align: ${node.attrs.alignment}; margin: 16px 0; position: relative; cursor: pointer; transition: all 0.2s; padding: 8px;`;
      dom.className = 'email-button-wrapper-node';

      const link = document.createElement('a');
      link.href = node.attrs.href || '#';
      link.textContent = node.attrs.text || 'Button';
      link.setAttribute('data-email-button', 'true');
      link.setAttribute('data-button-variant', node.attrs.variant);
      link.setAttribute('data-button-size', node.attrs.size);
      link.setAttribute('contenteditable', 'false');
      link.style.cssText = generateButtonStyle(node.attrs.variant, node.attrs.size);

      const inlineControls = document.createElement('div');
      inlineControls.className = 'inline-button-controls';
      inlineControls.setAttribute('contenteditable', 'false');
      inlineControls.style.cssText = `
        position: absolute;
        top: -8px;
        right: -8px;
        display: none;
        gap: 4px;
        z-index: 10;
        pointer-events: auto;
      `;

      const editButton = document.createElement('button');
      editButton.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 20h9"></path>
          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
        </svg>
      `;
      editButton.className = 'inline-edit-button';
      editButton.title = 'Bewerken';
      editButton.style.cssText = `
        width: 32px;
        height: 32px;
        border-radius: 6px;
        border: 2px solid #FF6B35;
        background: white;
        color: #FF6B35;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.15s;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      `;

      const deleteButton = document.createElement('button');
      deleteButton.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="3 6 5 6 21 6"></polyline>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
        </svg>
      `;
      deleteButton.className = 'inline-delete-button';
      deleteButton.title = 'Verwijderen';
      deleteButton.style.cssText = `
        width: 32px;
        height: 32px;
        border-radius: 6px;
        border: 2px solid #EF4444;
        background: white;
        color: #EF4444;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.15s;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      `;

      inlineControls.appendChild(editButton);
      inlineControls.appendChild(deleteButton);
      dom.appendChild(inlineControls);

      const handleClick = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (typeof getPos === 'function') {
          const pos = getPos();
          if (typeof pos === 'number') {
            editor.commands.setNodeSelection(pos);
          }
        }
      };

      const handleTouchStart = (e: TouchEvent) => {
        e.stopPropagation();

        if (typeof getPos === 'function') {
          const pos = getPos();
          if (typeof pos === 'number') {
            editor.commands.setNodeSelection(pos);
          }
        }
      };

      const handleEditClick = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (typeof getPos === 'function') {
          const pos = getPos();
          if (typeof pos === 'number') {
            editor.commands.setNodeSelection(pos);
            setTimeout(() => {
              const event = new CustomEvent('emailButtonEdit', { detail: { pos } });
              document.dispatchEvent(event);
            }, 10);
          }
        }
      };

      const handleDeleteClick = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (typeof getPos === 'function') {
          const pos = getPos();
          if (typeof pos === 'number') {
            editor.commands.setNodeSelection(pos);
            setTimeout(() => {
              const event = new CustomEvent('emailButtonDelete', { detail: { pos } });
              document.dispatchEvent(event);
            }, 10);
          }
        }
      };

      editButton.addEventListener('click', handleEditClick);
      deleteButton.addEventListener('click', handleDeleteClick);
      link.addEventListener('click', handleClick);
      link.addEventListener('touchstart', handleTouchStart);
      dom.addEventListener('click', handleClick);
      dom.addEventListener('touchstart', handleTouchStart);

      const handleMouseEnter = () => {
        if (window.innerWidth >= 768) {
          inlineControls.style.display = 'flex';
        }
      };

      const handleMouseLeave = () => {
        inlineControls.style.display = 'none';
      };

      dom.addEventListener('mouseenter', handleMouseEnter);
      dom.addEventListener('mouseleave', handleMouseLeave);

      editButton.addEventListener('mouseenter', () => {
        editButton.style.backgroundColor = '#FFF5F2';
        editButton.style.transform = 'scale(1.05)';
      });
      editButton.addEventListener('mouseleave', () => {
        editButton.style.backgroundColor = 'white';
        editButton.style.transform = 'scale(1)';
      });

      deleteButton.addEventListener('mouseenter', () => {
        deleteButton.style.backgroundColor = '#FEF2F2';
        deleteButton.style.transform = 'scale(1.05)';
      });
      deleteButton.addEventListener('mouseleave', () => {
        deleteButton.style.backgroundColor = 'white';
        deleteButton.style.transform = 'scale(1)';
      });

      const updateSelectionState = () => {
        const { selection } = editor.state;
        const pos = getPos();
        const isSelected = typeof pos === 'number' && selection.from === pos;

        if (isSelected) {
          dom.style.outline = '3px solid #FF6B35';
          dom.style.outlineOffset = '2px';
          dom.style.borderRadius = '8px';
          dom.style.backgroundColor = 'rgba(255, 107, 53, 0.05)';
        } else {
          dom.style.outline = 'none';
          dom.style.backgroundColor = 'transparent';
        }
      };

      editor.on('selectionUpdate', updateSelectionState);
      editor.on('transaction', updateSelectionState);

      updateSelectionState();

      dom.appendChild(link);

      return {
        dom,
        update: (updatedNode) => {
          if (updatedNode.type.name !== 'emailButton') return false;

          dom.style.textAlign = updatedNode.attrs.alignment;
          link.href = updatedNode.attrs.href || '#';
          link.textContent = updatedNode.attrs.text || 'Button';
          link.setAttribute('data-button-variant', updatedNode.attrs.variant);
          link.setAttribute('data-button-size', updatedNode.attrs.size);
          link.style.cssText = generateButtonStyle(updatedNode.attrs.variant, updatedNode.attrs.size);

          return true;
        },
        destroy: () => {
          editor.off('selectionUpdate', updateSelectionState);
          editor.off('transaction', updateSelectionState);
          editButton.removeEventListener('click', handleEditClick);
          deleteButton.removeEventListener('click', handleDeleteClick);
          link.removeEventListener('click', handleClick);
          link.removeEventListener('touchstart', handleTouchStart);
          dom.removeEventListener('click', handleClick);
          dom.removeEventListener('touchstart', handleTouchStart);
          dom.removeEventListener('mouseenter', handleMouseEnter);
          dom.removeEventListener('mouseleave', handleMouseLeave);
        },
      };
    };
  },

  addAttributes() {
    return {
      href: {
        default: null,
        parseHTML: (element) => {
          const link = element.querySelector('a');
          return link?.getAttribute('href') || element.getAttribute('href');
        },
      },
      text: {
        default: 'Button',
        parseHTML: (element) => {
          const link = element.querySelector('a');
          return link?.textContent?.trim() || element.textContent?.trim() || 'Button';
        },
      },
      variant: {
        default: 'primary',
        parseHTML: (element) => {
          const link = element.querySelector('a');
          const target = link || element;
          return target.getAttribute('data-button-variant') || 'primary';
        },
      },
      alignment: {
        default: 'left',
        parseHTML: (element) => {
          const align = element.style.textAlign || element.getAttribute('data-button-alignment');
          return align || 'left';
        },
      },
      size: {
        default: 'medium',
        parseHTML: (element) => {
          const link = element.querySelector('a');
          const target = link || element;
          return target.getAttribute('data-button-size') || 'medium';
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'p[data-email-button-wrapper]',
        priority: 100,
      },
      {
        tag: 'div[data-email-button-wrapper]',
        priority: 100,
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const { href, text, variant, alignment, size } = node.attrs;
    const buttonStyle = generateButtonStyle(variant, size);

    return [
      'p',
      mergeAttributes(
        {
          style: `text-align: ${alignment}; margin: 16px 0;`,
          'data-email-button-wrapper': 'true',
          'contenteditable': 'false',
        },
        this.options.HTMLAttributes
      ),
      [
        'a',
        mergeAttributes(HTMLAttributes, {
          href: href || '#',
          style: buttonStyle,
          'data-email-button': 'true',
          'data-button-variant': variant,
          'data-button-size': size,
          'contenteditable': 'false',
        }),
        text || 'Button',
      ],
    ];
  },

  addCommands() {
    return {
      setEmailButton:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              href: options.href,
              text: options.text,
              variant: options.variant || 'primary',
              alignment: options.alignment || 'left',
              size: options.size || 'medium',
            },
          });
        },

      updateEmailButton:
        (options) =>
        ({ tr, state, dispatch }) => {
          const { selection } = state;
          const node = state.doc.nodeAt(selection.from);

          if (!node || node.type.name !== this.name) {
            return false;
          }

          if (dispatch) {
            const attrs = { ...node.attrs, ...options };
            tr.setNodeMarkup(selection.from, undefined, attrs);
            dispatch(tr);
          }

          return true;
        },
    };
  },
});
